import { gt, alias } from '@ember/object/computed';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { hasMany } from 'ember-data/relationships';
import { computed } from '@ember/object';
import { FOUNDATION_LIQUID_TAG, FOUNDATION_CSS_LINK } from 'cio/utils/constants';
import { sanitize } from 'cio/utils/sanitize';
import { config } from 'cio/helpers/preview-whitelist';
import NewsletterModel from 'cio/models/newsletter';
import ActionModel from 'cio/models/action';
import TransactionalMessageModel from 'cio/models/transactional-message';
import DS from 'ember-data';

const defaultBody = `
<html>
  <head>
    <style type="text/css">
      /* Add your styles here, we'll process and inline all of your styles before sending */
    </style>
  </head>
  <body>
    <!-- We'll replace this content tag with whatever you write in your email -->
    {{ content }}

    <!--
      You can use the Customer.io unsubscribe link in all of your emails.
      We are able to track the unsubscribe to the specific email they
      unsubscribed from. You also have the option to include an unsubscribe
      link in your email header. You always have the option of whether to send
      to unsubscribed users or not, but use that ability with caution. To learn more,
      or if you want to handle unsubscribes yourself, please refer to the docs:
      https://learn.customer.io/documentation/unsubscribes.html
    -->
    <a href="{% unsubscribe_url %}" class="untracked">Unsubscribe</a>
  </body>
</html>
`.trim();

export default class LayoutModel extends Model {
  @attr('string') name!: string;
  @attr('string', { defaultValue: defaultBody }) body!: string;
  @alias('body') html!: string;
  @attr('number') created!: number;
  @attr('number') updated!: number;
  @attr('number') emailsCount!: number;
  @attr('number') campaignsCount!: number;
  @attr('number') broadcastsCount!: number;
  @attr('number') transactionalMessagesCount!: number;
  @attr('boolean', { defaultValue: false }) archived!: boolean;
  @attr('boolean', { defaultValue: false }) isDefault!: boolean;
  @attr('number') starterLayoutId!: number;

  // These relationships are retrieved async, via the model's "links" property
  // in the API call
  @hasMany('transactional-message', { async: true, inverse: null })
  transactionalMessages!: DS.PromiseManyArray<TransactionalMessageModel>;
  @hasMany('newsletter', { async: true, inverse: null }) newsletters!: DS.PromiseManyArray<NewsletterModel>;
  @hasMany('action', { async: true, inverse: null }) actions!: DS.PromiseManyArray<ActionModel>;
  // The template relationship is discovered by Ember Data: the layout model from
  // the API doesn't contain the template IDs of the associated models.
  // Ember Data makes the association from the "other side" of the relationship,
  // which contains a layout_id
  @hasMany('template', { async: false, inverse: 'layout' }) templates!: ActionModel[];

  @gt('emailsCount', 0) isUsed!: boolean;

  @computed('body')
  get previewBody() {
    let body = this.get('body') || '';
    return body.replace(FOUNDATION_LIQUID_TAG, FOUNDATION_CSS_LINK);
  }

  @computed('previewBody')
  get sanitizedPreviewBody() {
    return sanitize(this.get('previewBody'), config);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    layout: LayoutModel;
  }
}
